import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import Toggle from '@/components/base/Toggle';
import { formatDateTime } from '@/lib/format';
import {
  countPurgeable,
  KEEP_MONTH_OPTIONS,
  type RetentionSettings,
} from '@/lib/activityLogRetention';

interface RetentionCardProps {
  settings: RetentionSettings;
  purging: boolean;
  message: string | null;
  error: string | null;
  onUpdate: (patch: Partial<RetentionSettings>) => void;
  onPurge: (months: number | null) => Promise<number | null>;
  onDismiss: () => void;
}

function monthLabel(months: number): string {
  return months === 1 ? '1 mes' : `${months} meses`;
}

export default function RetentionCard({
  settings,
  purging,
  message,
  error,
  onUpdate,
  onPurge,
  onDismiss,
}: RetentionCardProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'old' | 'all'>('old');
  const [months, setMonths] = useState(settings.keepMonths);
  const [preview, setPreview] = useState<number | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoadingPreview(true);
    setPreview(null);

    countPurgeable(mode === 'all' ? null : months)
      .then((n) => {
        if (alive) setPreview(n);
      })
      .catch(() => {
        if (alive) setPreview(null);
      })
      .finally(() => {
        if (alive) setLoadingPreview(false);
      });

    return () => {
      alive = false;
    };
  }, [open, mode, months]);

  const handleConfirm = async () => {
    const deleted = await onPurge(mode === 'all' ? null : months);
    if (deleted !== null) setOpen(false);
  };

  const selectClass =
    'rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
            <i className="ri-delete-bin-6-line text-primary-600" /> Limpieza de registros
          </h2>
          <p className="mt-1 text-sm text-foreground-600 max-w-2xl">
            Solo se pueden vaciar los registros de <strong className="font-semibold">éxito</strong> y{' '}
            <strong className="font-semibold">aviso</strong>. Los errores y críticos se conservan
            siempre.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 text-secondary-900 px-3 py-1 text-xs font-medium whitespace-nowrap">
          <i className="ri-shield-check-line" /> Errores protegidos
        </span>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Automático */}
        <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground-950">Limpieza automática mensual</p>
              <p className="mt-1 text-xs text-foreground-600">
                Cada mes se borran los registros de éxito y aviso con más de{' '}
                {monthLabel(settings.keepMonths)}.
              </p>
            </div>
            <Toggle
              checked={settings.autoEnabled}
              onChange={(v) => onUpdate({ autoEnabled: v })}
              label="Limpieza automática mensual"
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label htmlFor="keep-months" className="text-xs font-medium text-foreground-600">
              Conservar
            </label>
            <select
              id="keep-months"
              value={settings.keepMonths}
              onChange={(e) => onUpdate({ keepMonths: Number(e.target.value) })}
              disabled={!settings.autoEnabled}
              className={selectClass}
            >
              {KEEP_MONTH_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Manual */}
        <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 flex flex-col">
          <p className="text-sm font-semibold text-foreground-950">Vaciado manual</p>
          <p className="mt-1 text-xs text-foreground-600">
            Última limpieza:{' '}
            <span className="font-medium text-foreground-800">
              {settings.lastRunAt ? formatDateTime(settings.lastRunAt) : 'nunca'}
            </span>
            {settings.lastPurgedCount > 0 && ` · ${settings.lastPurgedCount} registros eliminados`}
          </p>
          <button
            type="button"
            onClick={() => {
              setMode('old');
              setMonths(settings.keepMonths);
              setOpen(true);
            }}
            disabled={purging}
            className="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-700 transition whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed self-start"
          >
            {purging ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin" />
                Vaciando...
              </>
            ) : (
              <>
                <i className="ri-delete-bin-6-line" /> Vaciar ahora
              </>
            )}
          </button>
        </div>
      </div>

      {(message || error) && (
        <div
          className={`mt-4 rounded-md px-4 py-3 flex items-start gap-3 ${
            error
              ? 'border border-accent-300 bg-accent-50'
              : 'border border-secondary-200 bg-secondary-50'
          }`}
        >
          <i
            className={
              error ? 'ri-error-warning-line text-accent-700' : 'ri-checkbox-circle-line text-secondary-700'
            }
          />
          <p className="text-sm text-foreground-700 min-w-0">{error ?? message}</p>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Cerrar aviso"
            className="ml-auto w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition cursor-pointer shrink-0"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          if (!purging) setOpen(false);
        }}
        title="Vaciar registros de actividad"
        footer={
          <>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={purging}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={purging || loadingPreview || preview === 0}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {purging ? (
                <>
                  <span className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin" />
                  Vaciando...
                </>
              ) : (
                <>
                  <i className="ri-delete-bin-6-line" /> Confirmar vaciado
                </>
              )}
            </button>
          </>
        }
      >
        <p>
          Se eliminarán únicamente los registros de <strong>éxito</strong> y{' '}
          <strong>aviso</strong>. Los errores y críticos no se borran nunca.
        </p>

        <div className="mt-4 space-y-2">
          <label
            className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
              mode === 'old'
                ? 'border-primary-300 bg-primary-50'
                : 'border-background-300 bg-background-50 hover:bg-background-100'
            }`}
          >
            <input
              type="radio"
              name="purge-mode"
              checked={mode === 'old'}
              onChange={() => setMode('old')}
              className="mt-1 accent-primary-600 cursor-pointer"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-950">Solo los más antiguos</p>
              <p className="mt-0.5 text-xs text-foreground-600">
                Conserva lo reciente y borra lo que tenga más de:
              </p>
              <select
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                disabled={mode !== 'old'}
                onClick={(e) => e.stopPropagation()}
                className={`mt-2 ${selectClass}`}
              >
                {KEEP_MONTH_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
              mode === 'all'
                ? 'border-primary-300 bg-primary-50'
                : 'border-background-300 bg-background-50 hover:bg-background-100'
            }`}
          >
            <input
              type="radio"
              name="purge-mode"
              checked={mode === 'all'}
              onChange={() => setMode('all')}
              className="mt-1 accent-primary-600 cursor-pointer"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground-950">
                Todo el historial de éxito y aviso
              </p>
              <p className="mt-0.5 text-xs text-foreground-600">
                Vacía por completo estos registros, sin importar la fecha.
              </p>
            </div>
          </label>
        </div>

        <div className="mt-4 rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-700 flex items-center gap-2">
          {loadingPreview ? (
            <>
              <span className="w-4 h-4 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
              Calculando registros...
            </>
          ) : preview === null ? (
            <>
              <i className="ri-question-line" /> No se pudo calcular la cantidad.
            </>
          ) : (
            <>
              <i className="ri-database-2-line text-primary-700" />
              Se eliminarán <strong className="mx-1">{preview}</strong> registros.
            </>
          )}
        </div>
      </Modal>
    </section>
  );
}